import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link2, ShieldCheck, ShieldAlert, CreditCard, Download, Loader2, CheckCircle2, Copy, ExternalLink, Info } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const getPairingCode = (pairingLink?: string) => {
  if (!pairingLink) return null;
  try {
    const url = new URL(pairingLink);
    return url.searchParams.get('pairing_code');
  } catch {
    const match = pairingLink.match(/pairing_code=([^&]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }
};

const buildWpAdminPairingUrl = (domainName?: string | null, wpAdminUrl?: string | null, pairingCode?: string | null) => {
  if (!pairingCode) return null;
  if (wpAdminUrl) {
    try {
      const url = new URL(wpAdminUrl);
      return `${url.origin}${url.pathname.replace(/\/+$/, '')}/admin.php?page=shadowscan&pairing_code=${encodeURIComponent(pairingCode)}`;
    } catch {
      return null;
    }
  }
  if (!domainName) return null;
  try {
    const url = new URL(domainName.includes('://') ? domainName : `https://${domainName}`);
    return `${url.origin}/wp-admin/admin.php?page=shadowscan&pairing_code=${encodeURIComponent(pairingCode)}`;
  } catch {
    return null;
  }
};

const buildWpAdminUrlFromInput = (domainName?: string | null, rawValue?: string | null) => {
  const value = (rawValue || '').trim();
  if (!value) return null;
  if (value.startsWith('http://') || value.startsWith('https://')) {
    return value;
  }
  if (!domainName) return null;
  try {
    const baseUrl = new URL(domainName.includes('://') ? domainName : `https://${domainName}`);
    const path = value.startsWith('/') ? value : `/${value}`;
    return `${baseUrl.origin}${path}`;
  } catch {
    return null;
  }
};

const buildWpAdminPluginInstallUrl = (
  domainName?: string | null,
  wpAdminUrl?: string | null,
  wpAdminInput?: string,
) => {
  const adminBase =
    wpAdminUrl ||
    buildWpAdminUrlFromInput(domainName, wpAdminInput) ||
    (domainName ? buildWpAdminUrlFromInput(domainName, '/wp-admin/') : null);
  if (!adminBase) return null;
  try {
    const url = new URL(adminBase);
    let basePath = url.pathname;
    if (basePath.endsWith('.php')) {
      basePath = basePath.slice(0, basePath.lastIndexOf('/') + 1);
    } else if (!basePath.endsWith('/')) {
      basePath += '/';
    }
    const installUrl = new URL(url.origin);
    installUrl.pathname = `${basePath}plugin-install.php`;
    const params = new URLSearchParams(url.search);
    params.set('tab', 'upload');
    installUrl.search = params.toString();
    return installUrl.toString();
  } catch {
    return null;
  }
};

type SecureWebsiteOnboardingDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  domain?: {
    id: string;
    domain_name: string;
    wp_admin_url?: string | null;
  } | null;
  pairingLink?: string;
  heartbeatPluginZipUrl: string;
  paymentNeeded: boolean;
  stepsComplete?: boolean;
  onGeneratePairingLink?: () => void;
  onResumePayment?: () => void;
  onCheckStatus?: () => void;
  onWpAdminUrlUpdated?: (domainId: string, wpAdminUrl: string) => void;
};

type OnboardingStep = {
  id: string;
  title: string;
  description: string;
  action?: ReactNode;
  detail?: ReactNode;
};

export function SecureWebsiteOnboardingDialog({
  open,
  onOpenChange,
  domain,
  pairingLink,
  heartbeatPluginZipUrl,
  paymentNeeded,
  stepsComplete = false,
  onGeneratePairingLink,
  onResumePayment,
  onCheckStatus,
  onWpAdminUrlUpdated,
}: SecureWebsiteOnboardingDialogProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const [wpAdminInput, setWpAdminInput] = useState('');
  const [wpAdminChoice, setWpAdminChoice] = useState<'yes' | 'no'>('yes');
  const [savingWpAdmin, setSavingWpAdmin] = useState(false);

  useEffect(() => {
    if (open) {
      setStepIndex(0);
    }
  }, [open, domain?.id]);

  useEffect(() => {
    if (!open) return;
    setWpAdminInput(domain?.wp_admin_url || '');
    setWpAdminChoice(domain?.wp_admin_url ? 'no' : 'yes');
  }, [open, domain?.wp_admin_url, domain?.id]);

  const handleSaveWpAdminUrl = async (overrideValue?: string) => {
    if (!domain?.id) {
      toast.error('Domain not found.');
      return;
    }
    const normalized = buildWpAdminUrlFromInput(domain?.domain_name, overrideValue ?? wpAdminInput);
    if (!normalized) {
      toast.error('Enter your full WordPress admin URL.');
      return;
    }
    try {
      const url = new URL(normalized);
      if (!['http:', 'https:'].includes(url.protocol)) {
        throw new Error('Invalid protocol');
      }
    } catch {
      toast.error('Please enter a valid URL (including https://).');
      return;
    }

    setSavingWpAdmin(true);
    try {
      const { data, error } = await supabase.functions.invoke('update-domain-credentials', {
        body: {
          domainId: domain.id,
          wpAdminUrl: normalized,
        },
      });
      if (error || data?.error) throw new Error(error?.message || data?.error || 'Unable to update admin URL');
      toast.success('WP Admin URL saved.');
      onWpAdminUrlUpdated?.(domain.id, normalized);
    } catch (err: unknown) {
      console.error('update-domain-credentials failed:', err);
      const message = err instanceof Error ? err.message : 'Unable to save admin URL.';
      toast.error(message);
    } finally {
      setSavingWpAdmin(false);
    }
  };

  const steps = useMemo<OnboardingStep[]>(() => {
    const domainLabel = domain?.domain_name ? ` for ${domain.domain_name}` : '';
    const pairingCode = getPairingCode(pairingLink) || pairingLink || '';
    const wpAdminUrl = buildWpAdminPairingUrl(
      domain?.domain_name,
      wpAdminChoice === 'no'
        ? buildWpAdminUrlFromInput(domain?.domain_name, wpAdminInput)
        : domain?.wp_admin_url,
      pairingCode,
    );
    const pluginInstallUrl = buildWpAdminPluginInstallUrl(
      domain?.domain_name,
      domain?.wp_admin_url,
      wpAdminChoice === 'no' ? wpAdminInput : undefined,
    );
    const hasStoredWpAdminUrl = Boolean(domain?.wp_admin_url);
    const adminUrlForm = (
      <div className="rounded-md border border-border/50 bg-muted/40 p-3 space-y-2">
        <div className="flex items-center gap-2">
          <Label className="text-xs">Is your admin URL /wp-admin/ ?</Label>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-border/60 text-muted-foreground">
                <Info className="h-3 w-3" />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              This helps the Connector plugin open the correct admin page to finish security setup.
            </TooltipContent>
          </Tooltip>
        </div>
        <RadioGroup value={wpAdminChoice} onValueChange={(value) => setWpAdminChoice(value as 'yes' | 'no')}>
          <div className="flex items-center gap-2">
            <RadioGroupItem id="wp-admin-yes" value="yes" />
            <Label htmlFor="wp-admin-yes" className="text-sm">Yes</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem id="wp-admin-no" value="no" />
            <Label htmlFor="wp-admin-no" className="text-sm">No</Label>
          </div>
        </RadioGroup>
        {wpAdminChoice === 'yes' && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setWpAdminChoice('no')}
            >
              My admin URL changed
            </Button>
            {hasStoredWpAdminUrl && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSaveWpAdminUrl('/wp-admin/')}
                disabled={savingWpAdmin}
              >
                Use /wp-admin/ instead
              </Button>
            )}
          </div>
        )}
        {wpAdminChoice === 'no' && (
          <>
            <Label className="text-xs">Custom WP Admin URL (optional)</Label>
            <Input
              value={wpAdminInput}
              onChange={(event) => setWpAdminInput(event.target.value)}
              placeholder="/wp-login or https://yoursite.com/secret-admin/"
              disabled={savingWpAdmin}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSaveWpAdminUrl()}
                disabled={savingWpAdmin || !wpAdminInput.trim()}
              >
                {savingWpAdmin ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <ExternalLink className="h-4 w-4 mr-2" />
                )}
                Save admin URL
              </Button>
              {hasStoredWpAdminUrl && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSaveWpAdminUrl('/wp-admin/')}
                  disabled={savingWpAdmin}
                >
                  Use /wp-admin/ instead
                </Button>
              )}
              {hasStoredWpAdminUrl && (
                <span className="text-xs text-muted-foreground break-all">
                  Current: {domain?.wp_admin_url}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              If your WordPress admin login is custom, save it here so the pairing link opens the correct page.
            </p>
          </>
        )}
      </div>
    );
    return [
      {
        id: 'download',
        title: 'Step 1: Download plugin',
        description: `Download the ShadowScan plugin for your WordPress site${domainLabel}.`,
        action: (
          <Button asChild variant="outline" size="sm">
            <a href={heartbeatPluginZipUrl} target="_blank" rel="noreferrer">
              <Download className="h-4 w-4 mr-2" />
              Download plugin
            </a>
          </Button>
        ),
        detail: (
          <p className="text-xs text-muted-foreground">
            Keep the ZIP handy for the next step.
          </p>
        ),
      },
      {
        id: 'install',
        title: 'Step 2: Install plugin',
        description: 'Install the plugin in WordPress, then save your admin URL if it is custom.',
        action: pluginInstallUrl ? (
          <Button asChild variant="outline" size="sm">
            <a href={pluginInstallUrl} target="_blank" rel="noreferrer">
              <Download className="h-4 w-4 mr-2" />
              Install plugin
            </a>
          </Button>
        ) : (
          <Button variant="outline" size="sm" disabled>
            <Download className="h-4 w-4 mr-2" />
            Install plugin
          </Button>
        ),
        detail: (
          <div className="space-y-3">
            {adminUrlForm}
          </div>
        ),
      },
      {
        id: 'pair',
        title: 'Step 3: Pairing code',
        description: 'Generate a short-lived pairing code, then enter it in the ShadowScan plugin to link this website to your portal.',
        action: (
          <Button
            variant="outline"
            size="sm"
            onClick={onGeneratePairingLink}
            disabled={!onGeneratePairingLink}
            title="Expires in 10 minutes. This code links your WordPress plugin to this ShadowScan site entry."
          >
            <Link2 className="h-4 w-4 mr-2" />
            Generate pairing code
          </Button>
        ),
        detail: pairingLink ? (
          <div className="space-y-2">
            <p className="text-xs text-emerald-400 break-all">
              Pairing code: {pairingCode}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(pairingCode);
                    toast.success('Pairing code copied to clipboard.');
                  } catch {
                    window.prompt('Copy pairing code', pairingCode);
                  }
                }}
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy code
              </Button>
              {wpAdminUrl && (
                <Button asChild variant="outline" size="sm">
                  <a href={wpAdminUrl} target="_blank" rel="noreferrer">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open WP Admin
                  </a>
                </Button>
              )}
            </div>
            {paymentNeeded && (
              <p className="text-xs text-amber-400">
                Pairing is ready, but security setup will only begin once billing is active.
              </p>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">Codes expire after 10 minutes.</p>
        ),
      },
      {
        id: 'complete',
        title: paymentNeeded ? 'Step 4: All done - activate monitoring' : 'Step 4: All done!',
        description: paymentNeeded
          ? 'Your setup is complete. Monitoring begins as soon as billing is confirmed.'
          : 'Your setup is complete. We are verifying the connector and starting continuous monitoring.',
        action: paymentNeeded ? (
          <Button variant="outline" size="sm" onClick={onResumePayment} disabled={!onResumePayment}>
            <CreditCard className="h-4 w-4 mr-2" />
            Complete billing
          </Button>
        ) : (
          <div className="inline-flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Checking status… this can take a minute.
          </div>
        ),
        detail: paymentNeeded ? (
          <div className="inline-flex items-center text-xs text-amber-400">
            <ShieldAlert className="h-4 w-4 mr-2" />
            Once paid, monitoring activates automatically.
          </div>
        ) : (
          <div className="space-y-2 text-xs text-muted-foreground">
            <p>We’ll alert the ShadowScan team if anything needs attention.</p>
            {onCheckStatus && (
              <Button variant="outline" size="sm" onClick={onCheckStatus}>
                Check status
              </Button>
            )}
          </div>
        ),
      },
    ];
  }, [
    domain?.domain_name,
    domain?.wp_admin_url,
    heartbeatPluginZipUrl,
    onGeneratePairingLink,
    onResumePayment,
    pairingLink,
    paymentNeeded,
    onCheckStatus,
    handleSaveWpAdminUrl,
    wpAdminInput,
    savingWpAdmin,
    wpAdminChoice,
  ]);

  const currentStep = steps[stepIndex];
  const progress = Math.round(((stepIndex + 1) / steps.length) * 100);

  if (stepsComplete) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="cyber-card max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-mono">You’re all set</DialogTitle>
            <DialogDescription>Welcome to ShadowScan security monitoring.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 text-sm text-emerald-400">
              <CheckCircle2 className="h-4 w-4" />
              Connected and protected.
            </div>
            <p className="text-sm text-muted-foreground">
              That’s all you need to do. The ShadowScan team will handle the rest. If anything needs attention,
              we’ll alert our team and reach out.
            </p>
          </div>
          <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button size="sm" onClick={() => onOpenChange(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="cyber-card max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-mono">Secure Website Setup</DialogTitle>
          <DialogDescription>
            Step {stepIndex + 1} of {steps.length}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Progress value={progress} />
          <div className="space-y-2">
            <h3 className="text-base font-semibold">{currentStep.title}</h3>
            <p className="text-sm text-muted-foreground">{currentStep.description}</p>
            {currentStep.action}
            {currentStep.detail}
          </div>
        </div>
        <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setStepIndex((prev) => Math.max(prev - 1, 0))}
            disabled={stepIndex === 0}
          >
            Back
          </Button>
          <Button
            size="sm"
            onClick={() => {
              if (stepIndex === steps.length - 1) {
                onOpenChange(false);
              } else {
                setStepIndex((prev) => Math.min(prev + 1, steps.length - 1));
              }
            }}
          >
            {stepIndex === steps.length - 1 ? 'Done' : 'Next'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
